import type { ReactNode } from "react";
import { cx } from "../../utils/classnames";
import { toMoney } from "../../utils/currency";
import { IncomeSource } from "../../types/verification";
import { Radio } from "../forms/Radio";

export type IncomeStreamItem = IncomeSource["items"][number];

export type IncomeStreamCardLabels = {
  netMonthlyIncome?: string;
  startDate?: string;
  endDate?: string;
  payFrequency?: string;
  present?: string;
  unknownEmployer?: string;
};

const DEFAULT_LABELS: Required<IncomeStreamCardLabels> = {
  netMonthlyIncome: "Net monthly income",
  startDate: "Start date",
  endDate: "End date",
  payFrequency: "Pay frequency",
  present: "Present",
  unknownEmployer: "Unknown employer"
};

const defaultFormatDate = (iso: string) =>
  new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(new Date(iso));

const formatPayFrequency = (raw?: string | null) => {
  if (!raw) return "-";
  return raw
    .toLowerCase()
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join("-");
};

const getEmployerName = (item: IncomeStreamItem, fallback: string) =>
  item.data.employer?.name ||
  item.data.employment?.employer?.name ||
  item.data.income_description ||
  fallback;

export type IncomeStreamCardProps = {
  item: IncomeStreamItem;
  isSelected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  showPayFrequency?: boolean;
  trailing?: ReactNode;
  labels?: IncomeStreamCardLabels;
  formatDate?: (iso: string) => string;
  className?: string;
};

export const IncomeStreamCard = ({
  item,
  isSelected,
  onSelect,
  disabled,
  showPayFrequency,
  trailing,
  labels,
  formatDate = defaultFormatDate,
  className
}: IncomeStreamCardProps) => {
  const resolvedLabels = { ...DEFAULT_LABELS, ...labels };
  const employerName = getEmployerName(item, resolvedLabels.unknownEmployer);

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={cx(
        "w-full text-left p-5 rounded-lg border bg-white transition-colors",
        isSelected
          ? "border-primary-500"
          : "border-alpha-100 hover:border-alpha-200",
        disabled && "opacity-60 cursor-not-allowed",
        className
      )}
    >
      <div className="pb-4 flex flex-row gap-3 items-center">
        <Radio
          name={`income-stream-${item.id}`}
          id={`income-stream-${item.id}`}
          checked={isSelected}
          disabled={disabled}
          onChange={() => undefined}
        />
        <p className="text-content-1000 font-bold text-sm">{employerName}</p>
        {trailing}
      </div>
      <div className="border-t border-t-alpha-40 pt-4 space-y-1">
        <Row
          label={resolvedLabels.netMonthlyIncome}
          value={toMoney(item.data.net_monthly_income_amount) ?? "-"}
        />
        <Row
          label={resolvedLabels.startDate}
          value={item.data.start_date ? formatDate(item.data.start_date) : "-"}
        />
        <Row
          label={resolvedLabels.endDate}
          value={
            item.data.end_date
              ? formatDate(item.data.end_date)
              : resolvedLabels.present
          }
        />
        {showPayFrequency ? (
          <Row
            label={resolvedLabels.payFrequency}
            value={formatPayFrequency(item.data.pay_frequency)}
          />
        ) : null}
      </div>
    </button>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between">
    <p className="text-sm text-content-300 font-medium">{label}</p>
    <p className="text-sm text-content-1000 font-medium">{value}</p>
  </div>
);
